"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trackEvent } from "@/lib/analytics";
import { Mail, CheckCircle, Loader2 } from "lucide-react";

type NewsletterSignupProps = {
  variant?: "inline" | "stacked";
  className?: string;
  placeholder?: string;
  buttonText?: string;
  successMessage?: string;
};

export function NewsletterSignup({
  variant = "inline",
  className = "",
  placeholder = "Enter your email",
  buttonText = "Subscribe",
  successMessage = "Thanks for subscribing!",
}: NewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — real users never see or fill this
  const [status, setStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  // Captured once, via the lazy initializer, so the server can reject
  // submissions that arrive too quickly to be a human filling out the form.
  const [renderedAt] = useState(() => Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("error");
      setErrorMessage("Please enter a valid email address");
      return;
    }

    setStatus("pending");
    setErrorMessage("");

    try {
      const response = await fetch("/api/lead-capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          leadSource: "newsletter",
          website,
          formRenderedAt: renderedAt,
          referrer: typeof window !== "undefined" ? document.referrer : undefined,
          landingPage: typeof window !== "undefined" ? window.location.href : undefined,
          utmSource: typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("utm_source") : undefined,
          utmMedium: typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("utm_medium") : undefined,
          utmCampaign: typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("utm_campaign") : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        if (response.status === 429) {
          throw new Error("Too many requests. Please try again later.");
        }
        if (data?.error === "disposable_email_not_allowed") {
          throw new Error("Please use a valid work email address.");
        }
        throw new Error("Something went wrong. Please try again.");
      }

      setStatus("success");
      setEmail("");
      trackEvent({ event: "newsletter_signup_success" });
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong");
      trackEvent({ event: "newsletter_signup_error" });
    }
  };

  if (status === "success") {
    return (
      <div className={`flex items-center gap-2 text-primary ${className}`}>
        <CheckCircle className="h-5 w-5" />
        <span className="text-sm font-medium">{successMessage}</span>
      </div>
    );
  }

  const isInline = variant === "inline";

  return (
    <form
      onSubmit={handleSubmit}
      className={`${isInline ? "flex items-center gap-2" : "flex flex-col gap-3"} ${className}`}
    >
      {/* Honeypot: hidden from sighted users and screen readers; bots that
          auto-fill every input will populate it and get rejected server-side. */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden opacity-0"
      />
      <div className={`relative ${isInline ? "flex-1" : "w-full"}`}>
        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="email"
          placeholder={placeholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-9 bg-background/50 border-border/60"
          disabled={status === "pending"}
          aria-label="Email address"
        />
      </div>
      <Button
        type="submit"
        disabled={status === "pending"}
        className={`${isInline ? "" : "w-full"} bg-primary text-primary-foreground hover:bg-primary/90`}
      >
        {status === "pending" ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Subscribing...
          </>
        ) : (
          buttonText
        )}
      </Button>
      {status === "error" && errorMessage && (
        <p className="text-sm text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </form>
  );
}
